import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Label, SelectArrow } from 'modules/ui';
import { useDidMount } from 'modules/core';
import styles from './DatePicker.module.scss';

const DAY_END = { hour: 20, minute: 0, second: 0 };
const DAY_START = { hour: 4, minute: 0, second: 0 };

function DatePickerContainer({ children }) {
  return (
    <div
      style={{
        margin: '16px 0 36px',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}
    >
      {children}
    </div>
  );
}

export default function DatePickerInput({
  name,
  value,
  minDate,
  maxDate,
  error,
  label,
  placeholder,
  width,
  setFormValue,
  arrowClassName,
  setAsDayStart,
  required,
  optional,
  disabled,
  ...otherProps
}) {
  const didMount = useDidMount();

  const [selectedDate, setSelectedDate] = useState(
    value ? moment(value) : null,
  );

  function handleSetDate(isoDate) {
    setSelectedDate(moment(isoDate));

    if (setFormValue) {
      setFormValue(name, isoDate);
    }
  }

  useEffect(() => {
    if (didMount) {
      handleSetDate(value);
    }
  }, [value]);

  function handleDateChange(date) {
    date.set(setAsDayStart ? DAY_START : DAY_END);

    const isoDate = date.toISOString(true);
    handleSetDate(isoDate);
  }

  const hasErrorMessage = typeof error === 'string';

  return (
    <DatePickerContainer>
      {label && (
        <Label
          label={label}
          required={required}
          optional={optional}
          error={error}
          disabled={disabled}
        />
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          displayStaticWrapperAs="desktop"
          value={selectedDate}
          minDate={minDate}
          maxDate={maxDate}
          onChange={handleDateChange}
          placeholder={placeholder}
          slots={{
            openPickerIcon: SelectArrow,
          }}
          slotProps={{
            openPickerButton: {
              className: styles.openPickerButton,
            },
            popper: {
              className: styles.popper,
              disablePortal: true,
            },
            textField: {
              className: styles.textField,
              placeholder,
              style: otherProps.style,
            },
          }}
          {...otherProps}
        />
      </LocalizationProvider>
      {hasErrorMessage && (
        <div id={`${name}-error`} className={styles.errorMessage}>
          {error}
        </div>
      )}
    </DatePickerContainer>
  );
}

DatePickerInput.propTypes = {
  name: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  value: PropTypes.string,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  error: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setFormValue: PropTypes.func,
  arrowClassName: PropTypes.string,
  setAsDayStart: PropTypes.bool,
};

DatePickerInput.defaultProps = {
  placeholder: 'Pick a date',
  minDate: moment(),
  setAsDayStart: false,
};
